/**
 * mcKeys.ts — Phase 241 D-29/D-30: the ONE `humanizeMcKey()` in the repo, converging the two
 * byte-identical copies previously at `src/js/mc.ts:976-984` and `src/js/mc/heatmap.ts:477-485`,
 * plus D-30's `resolveMcLabel(label, rawKey)` label-preference choke point added alongside it.
 *
 * ZERO imports, deliberately (matching `utils/instants.ts` and `utils/detailPollFields.ts`'s own
 * stated constraint): `src/js/mc.ts` pulls in bootstrap/DataTables-adjacent modules and
 * `src/js/mc/heatmap.ts` pulls in Panzoom, so a plain `node file.ts` test can never import either
 * of them directly. Keeping this module dependency-free is the only way
 * `node tests/mc/mcKeys.test.ts` can exercise it with no jsdom and no bundler.
 *
 * This remains the required lock-step mirror of `App\Mc\McHumanize::minecraftKey()` (Phase 232's
 * D-06/D-09 origin) — any change here needs the matching PHP change in the same commit, exactly
 * as the two mirrors were required to move together before this convergence.
 *
 * `resolveMcLabel()` is the single choke point both renderers now route every label decision
 * through (D-30): `src/js/mc.ts`'s detail-poll dimension/biome rendering and
 * `src/js/mc/heatmap.ts`'s `renderDimensionTabs()` both call it rather than each writing their own
 * `label ?? humanizeMcKey(raw)` fallback expression. That gives the D-23 static guard
 * (`tools/check-mc-label-field-consumed-static.mjs`) one mechanical property to assert instead of
 * pattern-matching two hand-written fallback expressions, and means a future edit cannot silently
 * ignore a server-resolved label without deleting a visible `resolveMcLabel(` call.
 *
 * PD-8: `resolveMcLabel()` falls back to `humanizeMcKey(rawKey)` on an empty string, not just on
 * `null`/`undefined`. SC#2's guarantee is "never blank, never mangled, never dropped" — a wire
 * label that arrived as `''` must not render an empty cell. The server never actually emits `''`
 * (241-09 pins `dimensionLabel === null` strictly for a null raw key), so this branch is
 * defensive, not a live code path — recorded here so a reader does not mistake it for one.
 *
 * Phase 231 (LIVE-02)'s original docblock, carried forward verbatim in substance: any namespace
 * other than `minecraft:` (i.e. anything modded) is NEVER DROPPED. Phase 274 (D-07/D-08) softened
 * how that guarantee is honored — both halves of `<namespace>:<path>` are now mechanically
 * title-cased and joined by `': '` rather than the raw string carrying straight through — but the
 * namespace itself is still always present in the output.
 * That is SC#2's core promise and it must not be lost in this move.
 *
 * Phase 240 code review CR-02 is the cautionary tale for why this convergence is a pure MOVE, not
 * a merge: `utils/instants.ts`'s own two predecessor `relativeTime()` copies *differed*, and the
 * merged ladder shipped a live prod regression ("8,760 hours ago"). The two `humanizeMcKey()`
 * copies converged here were verified byte-identical by diff first (PD-9) — that verification, not
 * an assumption, is what removes CR-02's risk class from this move.
 */

/**
 * An EXACT mirror of PHP's `ucwords($s)` with its default delimiter set, and deliberately not the
 * `/\b\w/g` idiom this file used until Phase 274's code review (CR-03).
 *
 * PHP `ucwords()` upper-cases the first character of the string and any character that directly
 * follows one of its own default delimiters — space, `\t`, `\r`, `\n`, `\f`, `\v` — and nothing
 * else. `/\b\w/g` upper-cases after ANY non-word character, which additionally includes `-`, `.`,
 * `/` and every non-ASCII letter. All three punctuation characters are legal Minecraft
 * resource-location characters, and this repo already carries 124 modded `advancement_title` keys
 * and 45 modded `entity` keys containing `/`.
 *
 * Before Phase 274 both halves of this mirror pair returned a modded id RAW, so they agreed
 * trivially and the difference stayed latent. Phase 274 (D-07/D-08) routed the entire modded
 * namespace through the transform on both sides, turning a dormant `minecraft:`-only difference
 * into a live divergence across the whole modded key space (`some-mod:x` -> PHP `Some-mod: X` vs
 * JS `Some-Mod: X`). PHP is the canonical half because it is the shipped, server-rendered source
 * of truth for five live consumers outside chat, whereas this JS half is only ever reached
 * through `resolveMcLabel()`'s fallback arm.
 *
 * `toupper()` in PHP is byte-wise and ASCII-only under the C locale, so the character class here
 * is `[a-z]` and NOT a Unicode-aware one: `créate` must stay `Créate`, never `CréAte`, and
 * `Straße` must stay `Straße`, never `StraßE`. No UTF-8 byte of a multibyte character ever falls
 * in the ASCII `a-z` or whitespace ranges, so this stays exact for non-ASCII input.
 *
 * Enforced mechanically by `tools/check-mc-humanizer-mirror.mjs` and pinned from both sides by
 * `tests/fixtures/mc-key-humanization-corpus.json`.
 */
const ucwords = (s: string): string =>
    s.replace(/(^|[ \t\r\n\f\v])([a-z])/g, (_m, sep: string, c: string) => sep + c.toUpperCase());

// Phase 231 (LIVE-02) -- generic minecraft:* namespace-strip + title-case, mirroring
// detail.latte's own server-rendered humanization exactly (str_starts_with + substr(10) +
// str_replace('_',' ') + ucwords). Any other namespace (modded) is never dropped (Phase 274
// D-07/D-08): both halves of `<namespace>:<path>` are mechanically title-cased and joined by
// `': '`, so the namespace stays visible (`minecells:promenade` -> `Minecells: Promenade`).
export function humanizeMcKey(raw: string): string {
    const prefix = 'minecraft:';
    if (raw.startsWith(prefix)) {
        return ucwords(raw.slice(prefix.length).replace(/_/g, ' '));
    }

    const colonIndex = raw.indexOf(':');
    if (colonIndex <= 0 || colonIndex === raw.length - 1) return raw;

    const namespace = ucwords(raw.slice(0, colonIndex).replace(/_/g, ' '));
    const path = ucwords(raw.slice(colonIndex + 1).replace(/_/g, ' '));

    return namespace + ': ' + path;
}

/**
 * D-30's label-preference choke point. Prefers the server-resolved `label` (D-03's wire-carried
 * answer — D-01's "the server resolves, the wire carries the answer, the JS never re-derives
 * identity") and falls back to the humanizer only when no usable label arrived.
 *
 * PD-8's exact contract: returns `label` when it is a non-empty string; otherwise returns
 * `humanizeMcKey(rawKey)`. The empty-string case is decided here, once, rather than left to each
 * call site — three consumers each picking their own operator (`??` keeps `''`, `||` discards it)
 * would agree only by luck, which is the exact class of divergence D-30 exists to remove.
 */
export function resolveMcLabel(label: string | null | undefined, rawKey: string): string {
    if (typeof label === 'string' && label !== '') return label;
    return humanizeMcKey(rawKey);
}

/**
 * Phase 254 D-03 — the TS half of the dimension-to-pill-class mirror pair. Required lock-step PHP
 * mirror: `App\Mc\BluemapLink::dimensionPillClass()` — any change here needs the matching PHP
 * change in the same commit, exactly the same contract this file's own docblock (lines 12-14)
 * already states for `humanizeMcKey()`.
 *
 * Return type is deliberately `string`, not `string | null`, with `''` as the not-recognised
 * value — PHP returns `?string`/`null` because that is `mapIdForDimension()`'s established idiom
 * in that class, this returns `''` because 254-UI-SPEC.md §9.3.2 specifies it and every TS caller
 * uses the result in a falsy test or a `||` fallback. The contract both halves share, and the only
 * one callers may rely on: a recognised vanilla key yields exactly one of the three literal class
 * names, and everything else yields a FALSY value — never the Overworld class, never a class
 * derived from the raw key.
 *
 * Caller-side fallback rules this function deliberately does NOT own (named here so a future
 * reader does not push them into this function): heatmap tooltips substitute the tooltip-only
 * "unknown" fallback class on a falsy result (254-UI-SPEC.md §9.4), and the heatmap whole-map
 * button renders nothing at all on a falsy result (254-UI-SPEC.md §9.3.2).
 */
export function dimensionPillClass(dim: string): string {
    if (dim === 'minecraft:overworld') return 'mc-coord-pill--overworld';
    if (dim === 'minecraft:the_nether') return 'mc-coord-pill--nether';
    if (dim === 'minecraft:the_end') return 'mc-coord-pill--end';
    return '';
}
